import { SmepEventStateEnum } from '@nativewaves/nw-cloud-sdk/smep'
import { differenceInMinutes, isToday, isValid } from 'date-fns'

import { isClearedTime } from 'domains/Workspaces/Events/schedule'

/**
 * How close an `Upcoming` event's start is. Everything further out stays plain
 * `Upcoming` — the muted chip is the right amount of attention for next week.
 */
export type UpcomingProximity = 'imminent' | 'today'

/** Within this many minutes of its start — or already past it — an event counts as imminent. */
export const IMMINENT_THRESHOLD_MINUTES = 60

/**
 * Classifies how urgently an event's start reads in the list.
 *
 * Returns `null` for anything but a scheduled `Upcoming` event, and for starts
 * that are neither today nor within the imminent window. A start that has
 * already passed while the event is still `Upcoming` (a late hand-over) counts
 * as imminent, not as expired — it is the row an operator most needs to see.
 */
export const getUpcomingProximity = (
  state: SmepEventStateEnum,
  startTime?: string | null,
  now: Date = new Date(),
): UpcomingProximity | null => {
  if (state !== SmepEventStateEnum.Upcoming || isClearedTime(startTime)) {
    return null
  }

  const start = new Date(startTime as string)

  if (!isValid(start)) {
    return null
  }

  if (differenceInMinutes(start, now) <= IMMINENT_THRESHOLD_MINUTES) {
    return 'imminent'
  }

  return isToday(start) ? 'today' : null
}

/** Whole minutes until the start, floored at 0 for a start that already passed. */
export const getMinutesToStart = (startTime: string, now: Date = new Date()) =>
  Math.max(0, differenceInMinutes(new Date(startTime), now))
